import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Container from "../components/layout/Container";
import { fetchAbout } from "../services/api";
import { useLanguage } from "../contexts/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";

// Loading Skeleton Component
const AboutSkeleton = () => (
  <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-lg animate-pulse border border-white/30 overflow-hidden">
    <div className="p-8 lg:p-12 space-y-6">
      <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-8 rounded-full w-3/4"></div>
      <div className="space-y-3">
        <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-4 rounded-full w-full"></div>
        <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-4 rounded-full w-5/6"></div>
        <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-4 rounded-full w-4/5"></div>
        <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-4 rounded-full w-2/3"></div>
      </div>
      <div className="flex items-center space-x-4 pt-4">
        <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-16 w-16 rounded-2xl"></div>
        <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-16 w-16 rounded-2xl"></div>
        <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-16 w-16 rounded-2xl"></div>
      </div>
    </div>
  </div>
);

// About Card Component
const AboutCard = ({ item, index, t }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div 
      className="group bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border border-white/30"
      style={{ 
        animationDelay: `${index * 0.2}s`,
        animation: 'fadeInUp 0.8s ease-out forwards'
      }}
    >
      <div className="p-8 lg:p-12">
        {/* Title */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex-1">
            <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4 group-hover:text-red-600 transition-colors duration-300 leading-tight">
              {item.title}
            </h3>
            <div className="w-16 h-1 bg-gradient-to-r from-red-500 to-red-600 rounded-full group-hover:w-24 transition-all duration-300"></div>
          </div>
          
          {/* Small Decorative Image */}
          {item.image && (
            <div className="ml-6 hidden sm:block">
              <div className="relative w-20 h-20 lg:w-24 lg:h-24">
                {!imageLoaded && (
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-slate-300 border-t-red-500 rounded-full animate-spin"></div>
                  </div>
                )}
                <img 
                  src={item.image}
                  alt={item.title}
                  className={`w-full h-full object-cover rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-500 ${
                    imageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => setImageLoaded(true)}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-red-600/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div 
          className="prose lg:prose-lg max-w-none text-slate-700 leading-relaxed mb-8"
          dangerouslySetInnerHTML={{ __html: item.content }}
        />

        {/* Mobile Image (if exists) */}
        {item.image && (
          <div className="sm:hidden mb-6">
            <div className="relative w-full h-48 rounded-2xl overflow-hidden">
              {!imageLoaded && (
                <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  <div className="w-8 h-8 border-3 border-slate-300 border-t-red-500 rounded-full animate-spin"></div>
                </div>
              )}
              <img 
                src={item.image}
                alt={item.title}
                className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setImageLoaded(true)}
              />
            </div>
          </div>
        )}

        {/* Features/Benefits (if any) */}
        <div className="flex flex-wrap gap-3">
          {[t('pages.about.feature1') || 'Sifat', t('pages.about.feature2') || 'Ishonch', t('pages.about.feature3') || 'Professional'].map((feature, idx) => (
            <span 
              key={idx}
              className="inline-flex items-center bg-red-50 text-red-700 px-4 py-2 rounded-full text-sm font-semibold border border-red-200/50 hover:bg-red-100 transition-colors duration-200"
            >
              <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
              {feature}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ t }) => (
  <div className="text-center py-20">
    <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-16 max-w-lg mx-auto border border-slate-200/50 shadow-xl">
      <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-8">
        <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold text-slate-800 mb-4">{t('pages.about.noData') || "Ma'lumot topilmadi"}</h3>
      <p className="text-slate-600 mb-4 leading-relaxed">
        {t('pages.about.noDataDescription') || "Haqimizda bo'limida hali ma'lumot qo'shilmagan."}
      </p>
      <p className="text-sm text-slate-500 mb-8">
        {t('pages.about.addDataHint') || "Admin paneldan ma'lumot qo'shishingiz mumkin."}
      </p>
      <Link 
        to="/"
        className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
      >
        <span className="mr-2">{t('common.backHome') || 'Bosh sahifaga qaytish'}</span>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
      </Link>
    </div>
  </div>
);

// Company Values Section
const CompanyValues = ({ t }) => {
  const values = [
    {
      icon: "🎯",
      title: t('pages.about.value1Title') || "Maqsadga Yo'nalganlik",
      description: t('pages.about.value1Description') || "Har bir loyihada aniq maqsadlar qo'yamiz va ularga erishamiz"
    },
    {
      icon: "🤝",
      title: t('pages.about.value2Title') || "Ishonch",
      description: t('pages.about.value2Description') || "Mijozlarimiz bilan uzoq muddatli ishonchli munosabatlar quramiz"
    },
    {
      icon: "⚡",
      title: t('pages.about.value3Title') || "Innovatsiya",
      description: t('pages.about.value3Description') || "Eng yangi texnologiyalar va usullarni qo'llaymiz"
    },
    {
      icon: "🏆",
      title: t('pages.about.value4Title') || "Sifat",
      description: t('pages.about.value4Description') || "Har bir mahsulotda yuqori sifat standartlariga amal qilamiz"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {values.map((value, index) => (
        <div 
          key={index}
          className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/30 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-center"
          style={{ 
            animationDelay: `${index * 0.1}s`,
            animation: 'fadeInUp 0.6s ease-out forwards'
          }}
        >
          <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
            {value.icon}
          </div>
          <h4 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-red-600 transition-colors duration-300">
            {value.title}
          </h4>
          <p className="text-slate-600 text-sm leading-relaxed">
            {value.description}
          </p>
        </div>
      ))}
    </div>
  );
};

const About = () => {
  const [aboutData, setAboutData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();

  useEffect(() => {
    const loadAbout = async () => {
      try {
        setLoading(true);
        const data = await fetchAbout(currentLanguage);
        setAboutData(Array.isArray(data) ? data : [data]);
        setError('');
      } catch (error) {
        console.error('Haqida ma\'lumot yuklanmadi:', error);
        setError('Ma\'lumot yuklanishda xatolik yuz berdi');
        setAboutData([]);
      } finally {
        setLoading(false);
      }
    };

    loadAbout();
  }, [currentLanguage]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <Container className="py-8">
          <div className="py-12 lg:py-20">
            {/* Header Skeleton */}
            <div className="text-center mb-16">
              <div className="inline-block bg-red-100/80 rounded-full px-6 py-3 mb-6">
                <div className="w-24 h-4 bg-red-200 rounded animate-pulse"></div>
              </div>
              <div className="w-80 h-12 bg-slate-200 rounded mx-auto mb-4 animate-pulse"></div>
              <div className="w-96 h-6 bg-slate-100 rounded mx-auto animate-pulse"></div>
            </div>
            
            {/* Content Skeleton */}
            <div className="space-y-8">
              {Array.from({ length: 2 }, (_, i) => (
                <AboutSkeleton key={i} />
              ))}
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <Container className="py-8">
          <div className="text-center py-20">
            <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-12 max-w-lg mx-auto border border-red-200/50 shadow-xl">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-red-700 mb-4">{t('common.error') || 'Xatolik yuz berdi'}</h3>
              <p className="text-red-600 mb-8">{error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <span className="mr-2">{t('common.reload') || 'Qayta yuklash'}</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <Container className="py-8">
        <div className="py-12 lg:py-20">
          {/* Page Header */}
          <div className="text-center mb-20">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-slate-900 via-red-900 to-slate-900 bg-clip-text text-transparent leading-tight">
              {t('pages.about.title') || 'Biz Haqimizda'}
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
              {t('pages.about.subtitle') || 'Bruce Tank kompaniyasi - sizning ishonchli hamkoringiz. Professional yondashuv va yuqori sifat bilan xizmat ko\'rsatamiz.'}
            </p>
            <div className="w-20 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full"></div>
          </div>

          {/* About Content */}
          {aboutData.length > 0 ? (
            <div className="space-y-12 mb-20">
              {aboutData.map((item, index) => (
                <AboutCard key={index} item={item} index={index} t={t} />
              ))}
            </div>
          ) : (
            <EmptyState t={t} />
          )}

          {/* Company Values Section */}
          {aboutData.length > 0 && (
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-slate-900 to-red-900 bg-clip-text text-transparent">
                  {t('pages.about.ourValues') || 'Bizning Qadriyatlarimiz'}
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  {t('pages.about.valuesDescription') || "Biz ushbu qadriyatlar asosida ishlaymiz va mijozlarimizga xizmat ko'rsatamiz"}
                </p>
              </div>
              <CompanyValues t={t} />
            </div>
          )}

          {/* Stats Section */}
          {aboutData.length > 0 && (
            <div className="relative bg-gradient-to-r from-red-800 via-red-900 to-red-800 rounded-3xl p-12 lg:p-16 text-white overflow-hidden shadow-2xl">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}></div>
              </div>
              
              <div className="relative z-10">
                <div className="text-center mb-12">
                  <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                    {t('pages.about.achievements') || 'Bizning Yutuqlarimiz'}
                  </h2>
                  <p className="text-xl opacity-90 max-w-2xl mx-auto">
                    {t('pages.about.achievementsSubtitle') || 'Raqamlar bilan ifodalanadigan muvaffaqiyat va mijozlar ishonchi'}
                  </p>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                  {[
                    { number: "5+", label: t('pages.about.yearsExperience') || "Yillik Tajriba" },
                    { number: "1000+", label: t('pages.about.happyClients') || "Baxtli Mijozlar" },
                    { number: "50+", label: t('pages.about.productTypes') || "Mahsulot Turlari" },
                    { number: "24/7", label: t('pages.about.support') || "Qo'llab-quvvatlash" }
                  ].map((stat, index) => (
                    <div key={index} className="text-center group">
                      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 group-hover:bg-white/20 transition-all duration-300 transform group-hover:scale-105 border border-white/20">
                        <div className="text-3xl lg:text-4xl font-bold mb-2 text-white">
                          {stat.number}
                        </div>
                        <div className="text-sm lg:text-base opacity-90 font-medium">
                          {stat.label}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* CTA */}
                <div className="text-center mt-12">
                  <Link 
                    to="/contact"
                    className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    <span className="mr-2">{t('common.contactUs') || "Biz bilan bog'lanish"}</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>
      
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .border-3 {
          border-width: 3px;
        }
      `}</style>
    </div>
  );
};

export default About;
